import React, { useCallback, useEffect, useState } from 'react';
import { Handshake, Check, X, Plus, DollarSign, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { AdminPartnerInsights } from '../components/partner/AdminPartnerInsights';
import { authFetch } from '../lib/auth';

/* Admin — approve/reject affiliates, log referrals & commissions, mark payouts. */

const STATUS_CLS = {
  pending: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-red-500/10 text-red-300 border-red-500/30',
  revoked: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
};

async function api(path, options = {}) {
  const res = await authFetch(`/affiliates${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(typeof data?.detail === 'string' ? data.detail : `HTTP ${res.status}`), { status: res.status });
  return data;
}

function RefActionLogger({ rid, onDone }) {
  const [type, setType] = useState('contacted');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="text-[10.5px] font-bold text-[#FF9B4A] hover:text-[#ffb374] transition-colors shrink-0" data-testid={`ref-action-open-${rid}`}>
        + acción
      </button>
    );
  }
  return (
    <form
      className="w-full flex flex-wrap items-center gap-1.5 mt-1.5"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await api(`/referrals/${rid}/events`, { method: 'POST', body: JSON.stringify({ type, note }) });
          toast.success('Acción registrada — el partner la verá en su timeline ✅');
          setNote(''); setOpen(false);
          onDone && onDone();
        } catch (err) { toast.error(String(err.message)); }
      }}
      data-testid={`ref-action-form-${rid}`}
    >
      <select value={type} onChange={(e) => setType(e.target.value)}
        className="bg-[#1B1F24] border border-white/15 rounded-md text-[11px] px-1.5 py-1 outline-none" data-testid={`ref-action-type-${rid}`}>
        <option value="contacted">Contactado</option>
        <option value="proposal_sent">Propuesta enviada</option>
        <option value="demo">Demo / reunión</option>
        <option value="follow_up">Seguimiento</option>
        <option value="note">Nota</option>
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota para el partner (opcional)"
        className="flex-1 min-w-[120px] h-7 rounded-md bg-white/[0.06] border border-white/15 px-2 text-[11px] text-white outline-none focus:border-[#F77F11]" data-testid={`ref-action-note-${rid}`} />
      <button type="submit" className="rounded-md bg-[#F77F11] hover:bg-[#d8651a] px-2.5 h-7 text-[10.5px] font-bold transition-colors" data-testid={`ref-action-submit-${rid}`}>Log</button>
      <button type="button" onClick={() => setOpen(false)} className="text-[10.5px] text-neutral-500 hover:text-white px-1">✕</button>
    </form>
  );
}


function CommissionForm({ affId, onDone }) {
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const inputCls = 'h-9 rounded-lg bg-white/[0.06] border border-white/15 px-3 text-[12.5px] text-white outline-none focus:border-[#F77F11]';
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!amount) return;
        try {
          await api(`/${affId}/commissions`, { method: 'POST', body: JSON.stringify({ amount: parseFloat(amount), description: desc, period }) });
          toast.success('Commission recorded');
          setAmount(''); setDesc('');
          onDone();
        } catch (err) { toast.error(String(err.message)); }
      }}
      data-testid="admin-aff-commission-form"
    >
      <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$ 30%" type="number" step="0.01" min="0.01" className={`${inputCls} w-24`} data-testid="commission-amount" />
      <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Concepto (ej: 30% POS mensual)" className={`${inputCls} flex-1 min-w-[160px]`} data-testid="commission-desc" />
      <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07" className={`${inputCls} w-24`} data-testid="commission-period" />
      <button type="submit" className="inline-flex items-center gap-1 rounded-lg bg-[#F77F11] hover:bg-[#d8651a] px-3 h-9 text-[12px] font-semibold transition-colors" data-testid="commission-submit">
        <Plus className="w-3.5 h-3.5" /> Commission
      </button>
    </form>
  );
}

function ReferralForm({ affId, onDone }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const inputCls = 'h-9 rounded-lg bg-white/[0.06] border border-white/15 px-3 text-[12.5px] text-white outline-none focus:border-[#F77F11]';
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name && !company) return;
        try {
          await api(`/${affId}/referrals`, { method: 'POST', body: JSON.stringify({ name, company, status: 'lead' }) });
          toast.success('Referral added');
          setName(''); setCompany('');
          onDone();
        } catch (err) { toast.error(String(err.message)); }
      }}
      data-testid="admin-aff-referral-form"
    >
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Contacto" className={`${inputCls} w-32`} data-testid="referral-name" />
      <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Restaurante" className={`${inputCls} flex-1 min-w-[140px]`} data-testid="referral-company" />
      <button type="submit" className="inline-flex items-center gap-1 rounded-lg border border-white/20 hover:border-[#F77F11] px-3 h-9 text-[12px] font-semibold transition-colors" data-testid="referral-submit">
        <Plus className="w-3.5 h-3.5" /> Referido
      </button>
    </form>
  );
}

function AffiliateRow({ a, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadDetail = useCallback(async () => {
    try { setDetail(await api(`/${a.id}/detail`)); } catch { /* silent */ }
  }, [a.id]);

  useEffect(() => { if (open) loadDetail(); }, [open, loadDetail]);

  const setStatus = async (status) => {
    try {
      await api(`/${a.id}/status`, { method: 'POST', body: JSON.stringify({ status }) });
      toast.success(status === 'approved' ? `${a.name} aprobado ✅` : `${a.name}: ${status}`);
      onChanged();
    } catch (e) { toast.error(String(e.message)); }
  };

  const revoke = async () => {
    const reason = window.prompt('Motivo de la revocación (se informa a Eva y al equipo AI):', 'admin_decision');
    if (reason === null) return;
    try {
      await api(`/${a.id}/revoke`, { method: 'POST', body: JSON.stringify({ reason: reason || 'admin_decision' }) });
      toast.success(`${a.name} revocado 🚫 — Eva y el equipo AI informados. Historial conservado.`);
      onChanged();
    } catch (e) { toast.error(String(e.message)); }
  };

  const removeForever = async () => {
    if (!window.confirm(`⚠️ ELIMINAR DEFINITIVAMENTE a ${a.name} (${a.email})?\n\nEsta acción no se puede deshacer. Sus referidos y comisiones se conservan para contabilidad. Eva y el equipo AI serán informados.`)) return;
    try {
      await api(`/${a.id}`, { method: 'DELETE' });
      toast.success(`${a.name} eliminado definitivamente 🗑️ — Eva y el equipo AI informados.`);
      onChanged();
    } catch (e) { toast.error(String(e.message)); }
  };

  const refStatus = async (rid, status) => {
    try { await api(`/referrals/${rid}/status`, { method: 'POST', body: JSON.stringify({ status }) }); loadDetail(); onChanged(); }
    catch (e) { toast.error(String(e.message)); }
  };

  const markPaid = async (cid) => {
    try { await api(`/commissions/${cid}/paid`, { method: 'POST' }); toast.success('Marcada como pagada 💸'); loadDetail(); onChanged(); }
    catch (e) { toast.error(String(e.message)); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04]" data-testid={`admin-aff-row-${a.email}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex flex-wrap items-center gap-3 p-4 text-left">
        <div className="flex-1 min-w-[200px]">
          <p className="font-bold text-[14px]">{a.name} <span className="ml-1 text-neutral-500 font-normal text-[12px]">{a.email}</span></p>
          <p className="text-[11.5px] text-neutral-500">{a.company || '—'} · {a.phone || '—'} · ref: <span className="font-mono text-[#FF9B4A]">{a.ref_code}</span></p>
        </div>
        <div className="flex items-center gap-4 text-[11.5px] text-neutral-400 tabular-nums">
          <span>{a.stats.clicks} clicks</span>
          <span>{a.stats.referrals} refs</span>
          <span>{a.stats.customers} customers</span>
          <span className="text-white font-semibold">${a.stats.earned_total}</span>
        </div>
        <span className={`text-[10px] font-bold uppercase rounded-full border px-2.5 py-1 ${STATUS_CLS[a.status]}`} data-testid={`aff-status-${a.email}`}>{a.status}</span>
      </button>

      {open && (
        <div className="border-t border-white/[0.07] p-4 space-y-4">
          {a.audience && <p className="text-[12px] text-neutral-400"><b className="text-neutral-300">Audiencia:</b> {a.audience}</p>}
          <div className="flex flex-wrap gap-2">
            {a.status !== 'approved' && (
              <button onClick={() => setStatus('approved')} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 h-9 text-[12px] font-semibold transition-colors" data-testid={`approve-${a.email}`}>
                <Check className="w-3.5 h-3.5" /> Aprobar
              </button>
            )}
            {a.status !== 'rejected' && (
              <button onClick={() => setStatus('rejected')} className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 px-3 h-9 text-[12px] font-semibold transition-colors" data-testid={`reject-${a.email}`}>
                <X className="w-3.5 h-3.5" /> Rechazar
              </button>
            )}
            {a.status === 'approved' && (
              <button onClick={revoke} className="inline-flex items-center gap-1 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-3 h-9 text-[12px] font-semibold transition-colors" data-testid={`revoke-${a.email}`}>
                🚫 Revocar acceso
              </button>
            )}
            {a.status === 'revoked' && (
              <button onClick={() => setStatus('approved')} className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-3 h-9 text-[12px] font-semibold transition-colors" data-testid={`reinstate-${a.email}`}>
                <Check className="w-3.5 h-3.5" /> Reinstaurar
              </button>
            )}
            <button onClick={removeForever} className="inline-flex items-center gap-1 rounded-lg border border-red-500/60 bg-red-500/10 text-red-300 hover:bg-red-500/25 px-3 h-9 text-[12px] font-bold transition-colors" data-testid={`delete-${a.email}`}>
              <X className="w-3.5 h-3.5" /> Eliminar definitivamente
            </button>
          </div>
          {a.status === 'approved' && (
            <>
              <ReferralForm affId={a.id} onDone={() => { loadDetail(); onChanged(); }} />
              <CommissionForm affId={a.id} onDone={() => { loadDetail(); onChanged(); }} />
            </>
          )}
          {detail && (
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">Referidos</p>
                {detail.referrals.length === 0 ? <p className="text-[12px] text-neutral-600">No referrals yet.</p> :
                  detail.referrals.map((r) => (
                    <div key={r.id} className="rounded-lg bg-white/[0.03] px-3 py-2 mb-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="flex-1 text-[12.5px] truncate">{r.company || r.name || r.email}</p>
                        <select value={r.status} onChange={(e) => refStatus(r.id, e.target.value)}
                          className="bg-[#1B1F24] border border-white/15 rounded-md text-[11px] px-1.5 py-1 outline-none" data-testid={`referral-status-${r.id}`}>
                          <option value="lead">lead</option><option value="customer">customer</option><option value="lost">lost</option>
                        </select>
                        <RefActionLogger rid={r.id} onDone={loadDetail} />
                      </div>
                    </div>
                  ))}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">Comisiones</p>
                {detail.commissions.length === 0 ? <p className="text-[12px] text-neutral-600">No commissions yet.</p> :
                  detail.commissions.map((cm) => (
                    <div key={cm.id} className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2 mb-1.5">
                      <p className="flex-1 text-[12.5px] truncate">{cm.description || cm.period} <span className="text-neutral-500">({cm.period})</span></p>
                      <p className="font-bold text-[12.5px] tabular-nums">${cm.amount}</p>
                      {cm.status === 'paid' ? (
                        <span className="text-[10px] font-bold uppercase text-emerald-400">paid</span>
                      ) : (
                        <button onClick={() => markPaid(cm.id)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 px-2 py-1 text-[10.5px] font-bold transition-colors" data-testid={`pay-${cm.id}`}>
                          <DollarSign className="w-3 h-3" /> Pagar
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminAffiliatesPage() {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api('');
      setRows(d.affiliates);
    } catch (e) {
      if (e.status === 401) window.location.href = '/login?next=%2Fadmin%2Faffiliates';
      else toast.error(String(e.message));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pt-32 pb-20" data-testid="admin-affiliates-page">
      <SEO path="/admin/affiliates" title="Affiliates | Kitxens AI OS" noindex />
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-[#FF9B4A] text-[10px] tracking-[0.24em] uppercase font-bold"><Handshake className="w-3.5 h-3.5" /> Kitxens AI OS</p>
            <h1 className="mt-2 text-3xl font-bold tracking-[-0.02em]">Affiliate Network</h1>
            <p className="mt-1 text-[13px] text-neutral-500">30% recurring to the affiliate · 10% off for 1 year to the referral · manual approval</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-white transition-colors" data-testid="admin-aff-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <AdminPartnerInsights />
        <p className="mt-10 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Applications & commissions management</p>
        <div className="mt-3 space-y-3">
          {rows === null ? <p className="text-neutral-500 text-sm">Cargando…</p> :
            rows.length === 0 ? <p className="text-neutral-500 text-sm" data-testid="admin-aff-empty">No applications yet. Share kitxens.com/affiliates 🚀</p> :
              rows.map((a) => <AffiliateRow key={a.id} a={a} onChanged={load} />)}
        </div>
      </div>
    </div>
  );
}
